import { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Sliders } from "lucide-react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { DownloadsBlock } from "@/components/sections/DownloadsBlock";
import { PageError, PageLoader } from "@/components/ui/PageStates";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { useContent } from "@/lib/content-context";

export default function DownloadsPage() {
  const { data, loading, error } = useContent();

  useEffect(() => {
    document.title = "Downloads and presets - Goldmap EA";
    window.scrollTo({ top: 0 });
  }, []);

  if (loading) {
    return (
      <SiteLayout config={null}>
        <PageLoader />
      </SiteLayout>
    );
  }
  if (error || !data) {
    return (
      <SiteLayout config={null}>
        <PageError onRetry={() => window.location.reload()} />
      </SiteLayout>
    );
  }

  const presets = data.presets || [];

  return (
    <SiteLayout config={data.config} availability={data.availability}>
      <section className="gm-hero-bg relative pb-10 pt-12">
        <div className="gm-container">
          <Link
            to="/"
            data-testid="downloads-back-link"
            className="inline-flex items-center gap-2 text-xs font-semibold"
            style={{ color: "var(--gm-muted)" }}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>
          <h1 className="mt-5 text-3xl font-extrabold text-white sm:text-4xl">
            Downloads and presets
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed" style={{ color: "var(--gm-muted)" }}>
            EA builds for both terminals, plus ready-made .set files so you tak payah
            tune everything from zero.
          </p>
        </div>
      </section>

      <DownloadsBlock
        builds={data.downloads?.builds || []}
        config={data.config}
        showPresetLink={false}
      />

      <section className="gm-section pt-0">
        <div className="gm-container">
          <SectionHeader
            eyebrow="Presets"
            title="Preset .set files"
            subtitle="Tuned around a 50K reference capital. If your account is smaller, scale the lot settings down proportionally."
            testId="presets-header"
          />

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {presets.map((p) => (
              <article
                key={p.slug}
                className="gm-card gm-card-hover flex items-center gap-4"
                data-testid={`preset-card-${p.slug}`}
              >
                <span
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                  style={{
                    background: "rgba(246,196,83,0.1)",
                    border: "1px solid rgba(246,196,83,0.3)",
                  }}
                >
                  <Sliders className="h-5 w-5" style={{ color: "var(--gm-warning)" }} />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-white">{p.name}</h3>
                  <p className="font-mono-gm mt-1 truncate text-xs" style={{ color: "var(--gm-muted-2)" }}>
                    {p.filename}
                  </p>
                  <p className="mt-1 text-xs" style={{ color: "var(--gm-muted)" }}>
                    {p.note}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    toast.info(
                      "Presets are sent together with your licence. Ping us on Telegram with your reference code."
                    )
                  }
                  data-testid={`preset-download-${p.slug}`}
                  aria-label={`Download ${p.filename}`}
                  className="gm-btn-ghost inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                >
                  <Download className="h-4 w-4" />
                </button>
              </article>
            ))}
          </div>

          <p
            className="mt-6 rounded-xl p-4 text-xs leading-relaxed"
            data-testid="presets-note"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "var(--gm-muted)",
            }}
          >
            A preset is only a starting point, not a magic setting. Always test on demo
            or a cent account first, and adjust the lot and equity limits to match your
            own risk appetite.
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}
